/**
 * Der Themenaufbau des Geräteprotokolls.
 *
 * **Hier, weil ihn drei Seiten brauchen.** Der Ingest hört zu, die API sendet
 * die Gegenrichtung, und der Kopplungsablauf teilt dem Gerät seine Basis mit —
 * `apps/` importieren sich nie gegenseitig, also gehört das Gemeinsame in ein
 * Package. Bis HM-047 stand die Version an vier Stellen als Zeichenkette; beim
 * Wechsel auf `v2` wäre eine davon stehen geblieben, und die API hätte auf ein
 * Thema gesendet, das niemand mehr abonniert.
 *
 * Es ist Protokollwissen, kein I/O: reine Zeichenkettenbildung, ohne Client und
 * ohne Netz. Damit steht es hier richtig.
 *
 * Vollständige Beschreibung: `docs/device/mqtt-protocol.md`.
 */

export const TOPIC_PREFIX = "hm";

/**
 * **`v2` seit HM-047.** Die Telemetrie adressiert Werte seither über den
 * Datenpunkt-Schlüssel statt über Kanal plus OBIS-Kennzahl (ADR 0020).
 *
 * `v1` wird **nicht** mehr angenommen und auch nicht übersetzt: Eine
 * Übersetzung müsste `ch`+`obis` auf Schlüssel abbilden, die es v1-seitig nicht
 * gibt — also raten (Regel 9). Ein Gerät mit alter Firmware scheitert dadurch
 * laut und wird gezählt, statt still Werte zu verlieren.
 */
export const PROTOCOL_VERSION = "v2";

/** Die Basis, unter der ein Gerät sendet und empfängt. */
export function deviceBaseTopic(deviceId: string): string {
  return `${TOPIC_PREFIX}/${PROTOCOL_VERSION}/${deviceId}`;
}

/** Ein einzelnes Thema dieses Geräts. */
export function deviceTopic(deviceId: string, kind: string, ...rest: readonly string[]): string {
  return [deviceBaseTopic(deviceId), kind, ...rest].join("/");
}

/**
 * Das Thema eines einzelnen **Datenpunkts** in der Gegenrichtung (ADR 0020):
 * `hm/v2/{device}/dp/{key}`.
 *
 * Die einzige Art mit einem Unterthema — und das ist hier richtig: Ein
 * Schreibauftrag trifft genau einen Datenpunkt, während `config` und `command`
 * das ganze Gerät betreffen.
 */
export function datapointTopic(deviceId: string, key: string): string {
  return deviceTopic(deviceId, "dp", key);
}

/** Muster, das der Ingest abonniert: alles, was Geräte senden. */
export const UPSTREAM_SUBSCRIPTION = `${TOPIC_PREFIX}/${PROTOCOL_VERSION}/+/#`;

/**
 * Zugriffsregel für den Broker: Ein Gerät darf ausschließlich unter seiner
 * eigenen Kennung veröffentlichen.
 *
 * Ohne diese Beschränkung könnte ein übernommenes Gerät Werte für fremde Geräte
 * einliefern — der Broker prüft nichts von sich aus. Die Regel hängt an der
 * Gerätekennung und **nur** an ihr: Sie überlebt damit jeden Wechsel der Anlage
 * und des Besitzers, ohne dass jemand sie nachziehen müsste.
 */
export function devicePublishPattern(deviceId: string): string {
  return `${deviceBaseTopic(deviceId)}/#`;
}

export function deviceSubscribePattern(deviceId: string): string {
  return `${deviceBaseTopic(deviceId)}/+`;
}

/* --------------------------------------------------------------------------
 * Das Gateway und seine Knoten (HM-109, ADR 0031)
 * ----------------------------------------------------------------------- */

/**
 * Die Themenebene, unter der ein Gateway für **ein Gerät hinter sich** spricht:
 * `hm/v2/{gateway}/node/{key}/{art}`.
 *
 * **Alles läuft unter der Kennung des Gateways**, und das ist die tragende
 * Entscheidung dieses Aufbaus. Ein Knoten hat keine eigene Verbindung und keine
 * eigenen Zugangsdaten; er könnte gar nicht unter eigener Kennung senden, ohne
 * dass jemand ihm eine Zugriffsregel am Broker einrichtet. So bleibt
 * `devicePublishPattern` unverändert: Ein Gateway darf unter seiner eigenen
 * Kennung veröffentlichen — und die Knoten stehen darunter.
 *
 * **Der Knotenschlüssel ist gatewaylokal, nicht unsere Kennung.** Das Gateway
 * kennt die Gerätekennung eines Knotens erst, nachdem ein Mensch ihn zugelassen
 * hat; müsste es sie im Thema führen, könnte es vor der Zulassung nichts
 * melden — auch nicht den Fund selbst. Die Abbildung
 * `(gateway, key) → devices.id` steht in der Datenbank, wo jede andere
 * Zuordnung dieses Produkts auch steht.
 */
export const GATEWAY_NODE_SEGMENT = "node";

export function gatewayNodeTopic(gatewayId: string, nodeKey: string, kind: string): string {
  return deviceTopic(gatewayId, GATEWAY_NODE_SEGMENT, nodeKey, kind);
}

/**
 * Das Thema eines **Datenpunkts eines Knotens** in der Gegenrichtung (HM-110,
 * ADR 0032): `hm/v2/{gateway}/node/{key}/dp/{dpKey}`.
 *
 * Dieselbe Form wie `datapointTopic`, nur eine Ebene tiefer — und **das einzige,
 * was die Cloud an einen Knoten sendet**. `config`, `command` und `ota` handeln
 * vom Gerät am Broker, und das ist das Gateway; ein Knoten hat weder eine
 * Konfiguration, die die Cloud kennt, noch eine Firmware, die sie erneuern
 * könnte. Ein Schreibauftrag dagegen trifft einen Datenpunkt, den der Knoten
 * selbst als schreibbar gemeldet hat — Lademodus, Stromgrenze, Phasen. Die
 * Quittung kommt unter `hm/v2/{gateway}/node/{key}/ack` zurück, damit sie am
 * Knoten landet, dem der Auftrag gehört, und nicht am Gateway.
 */
export function gatewayNodeDatapointTopic(
  gatewayId: string,
  nodeKey: string,
  dpKey: string,
): string {
  return deviceTopic(gatewayId, GATEWAY_NODE_SEGMENT, nodeKey, "dp", dpKey);
}

/**
 * Was ein Gateway abonniert, um Schreibaufträge an seine Knoten zu bekommen:
 * `hm/v2/{gateway}/node/+/dp/+`.
 *
 * **`deviceSubscribePattern` reicht genau eine Ebene tief** — `allow`, `config`,
 * `command` — und deckt dieses Thema nicht. Der Broker prüft ein Abonnement
 * gegen die Zugriffsregel; ohne dieses Muster in der Regel schlägt es still
 * fehl, und die Wallbox sähe aus, als ignorierte sie jeden Auftrag. Das Muster
 * steht in `infra/mosquitto/mosquitto.conf.template` als dritte Zeile der
 * Zugriffsabfrage; der Test dort hält beide zusammen.
 */
export function gatewayNodeOrdersPattern(gatewayId: string): string {
  return deviceTopic(gatewayId, GATEWAY_NODE_SEGMENT, "+", "dp", "+");
}

/**
 * Der **Fund**: was das Gateway in seinem Netz gefunden hat.
 * `hm/v2/{gateway}/nodes`, behalten.
 *
 * Behalten, weil er ein Stand ist und kein Ereignis — dieselbe Begründung wie
 * beim Datenpunkt-Katalog. Nach einem Neustart des Ingest muss die Liste wieder
 * da sein, ohne dass jemand das Gateway darum bitten müsste. Und wie bei jeder
 * behaltenen Nachricht folgt aus ihrem Empfang **keine Anwesenheit** (HM-039).
 */
export function gatewayNodesTopic(gatewayId: string): string {
  return deviceTopic(gatewayId, "nodes");
}

/**
 * Die **Zulassung**: welche Knoten senden dürfen.
 * `hm/v2/{gateway}/allow`, behalten, von der API geschrieben.
 *
 * **Sie ist eine Auskunft und kein Schutz.** Durchgesetzt wird die Zulassung im
 * Ingest, der einen Wert zu einem nicht zugelassenen Knoten zählt und verwirft;
 * ein Gateway, das die Liste ignoriert, kommt damit keinen Schritt weiter. Was
 * sie leistet, ist das Gegenteil eines Schutzes: Sie erspart dem Gateway das
 * Senden ins Leere — und dem Nutzer die Frage, warum ein Gerät, das er abgelehnt
 * hat, weiter im Protokoll seines Gateways auftaucht.
 *
 * Behalten und nicht als Auftrag: Sie ist ein Stand, der auch für ein Gateway
 * gelten muss, das gerade nicht verbunden ist. Ein Auftrag (`command`) wäre
 * genau der Fall, den ADR 0019 für Handlungen vorsieht — dies ist keine.
 */
export function gatewayAllowTopic(gatewayId: string): string {
  return deviceTopic(gatewayId, "allow");
}

/**
 * Der reservierte Bezeichner, unter dem **die Cloud** über sich selbst spricht
 * (ADR 0022): `hm/v2/cloud/status`.
 *
 * Er steht an der Stelle des Geräts, und das ist kein Trick, sondern die
 * einzige Form, die ein Gerät ohne neue Zugriffsregel abonnieren kann — die
 * Regel des Brokers hängt an genau dieser Ebene. Eine Verwechslung ist
 * ausgeschlossen: An dieser Stelle steht sonst `devices.id`, eine UUID, und
 * keine UUID lautet `cloud`. Der Ingest weist eine Nachricht unter diesem
 * Bezeichner trotzdem ausdrücklich ab (`parseTopic`), statt sich auf die
 * Unmöglichkeit zu verlassen: Wer sich als Gerät anmeldet, wählt seinen
 * Benutzernamen nicht selbst, aber ein Fehler in der Zugriffsregel wäre sonst
 * ein Weg, Werte unter der Kennung der Cloud einzuliefern.
 */
export const CLOUD_IDENTIFIER = "cloud";

/**
 * Das Thema, auf dem die Annahmestelle ihre **Aufnahmebereitschaft** meldet.
 *
 * Behalten (retained) und mit Testament (LWT) — beides ist tragend: Ein Gerät,
 * das sich verbindet, bekommt den letzten Stand sofort zugestellt und muss auf
 * nichts warten, und ein abgestürzter Ingest wird vom Broker abgemeldet, ohne
 * dass er es selbst noch sagen könnte.
 */
export function cloudPresenceTopic(): string {
  return `${TOPIC_PREFIX}/${PROTOCOL_VERSION}/${CLOUD_IDENTIFIER}/status`;
}

/**
 * Ob dieser Bezeichner an der Geräteebene ein Gerät meint.
 *
 * Die Frage entsteht, weil der Ingest `hm/v2/+/#` abonniert und damit seine
 * **eigene** Präsenznachricht wieder hereinbekommt. Ohne diese Prüfung suchte
 * er ein Gerät namens `cloud`, fände keines und zählte bei jedem
 * Verbindungsaufbau ein `device.unknown` — ein Fehlerzähler, der wächst, ohne
 * dass irgendetwas fehlt.
 */
export function isDeviceIdentifier(identifier: string): boolean {
  return identifier !== CLOUD_IDENTIFIER;
}
